/**
 * StoryCreationWizard Component
 *
 * Main container for the story creation wizard.
 * Full-screen experience with back button, self-contained auth/profile logic,
 * session recovery on refresh, and wizard state management.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { faArrowLeftLong } from '@fortawesome/pro-regular-svg-icons/faArrowLeftLong'
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Typography,
} from '@mui/material'

import { PlotOutline, PremadeStoryConceptDetail, SessionSubmitResponse, StoryCreationSessionStatusEnum } from '@/shared/api/models'
import { useAccountReaderProfilesList } from '@/shared/api/generated/reader-profiles/reader-profiles'
import { useDrawingStylesList } from '@/shared/api/generated/drawing-styles/drawing-styles'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { IconButton } from '@/components/IconButton/IconButton'
import { pxToRem } from '@/shared/styles/themes'

import { StoryCreationProgress } from './StoryCreationProgress'
import {
  StepCharacterSelection,
  StepFormatSelection,
  StepLanguageSelection,
  StepStorySelection,
  StepSubmission,
} from './steps'
import { useStoryCreationSession } from './hooks'
import { SelectedCharacters, STEP_ORDER, StoryCreationStep } from './types'

export const StoryCreationWizard: React.FC = () => {
  const router = useRouter()
  const { isAuthenticated, isAuthenticating } = usePixieWorldAuthContext()
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  // Fetch reader profiles
  const {
    data: readerProfiles,
    isLoading: isLoadingProfiles,
    error: profilesError,
  } = useAccountReaderProfilesList({
    query: {
      enabled: isAuthenticated,
    },
  })

  // Auto-select first profile if only one exists
  useEffect(() => {
    if (readerProfiles?.length === 1 && !selectedProfileId) {
      setSelectedProfileId(readerProfiles[0].id)
    }
  }, [readerProfiles, selectedProfileId])

  const profileId = selectedProfileId || readerProfiles?.[0]?.id

  // --- Auth / Profile gates ---

  // Defer auth-dependent rendering until after client mount to prevent hydration mismatch
  if (!hasMounted || isAuthenticating) {
    return (
      <StyledPageWrapper>
        <TopBar />
        <StyledLoadingContainer>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading...
          </Typography>
        </StyledLoadingContainer>
      </StyledPageWrapper>
    )
  }

  if (!isAuthenticated) {
    return (
      <StyledPageWrapper>
        <TopBar />
        <StyledCenteredContent>
          <Typography variant="h5" gutterBottom>
            Sign in to continue
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You need to be signed in to create a personalized story.
          </Typography>
          <Button
            primary
            href={`/account/login?redirect=${encodeURIComponent(router.asPath)}`}
          >
            Sign In
          </Button>
        </StyledCenteredContent>
      </StyledPageWrapper>
    )
  }

  if (isLoadingProfiles) {
    return (
      <StyledPageWrapper>
        <TopBar />
        <StyledLoadingContainer>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading your profiles...
          </Typography>
        </StyledLoadingContainer>
      </StyledPageWrapper>
    )
  }

  if (profilesError) {
    return (
      <StyledPageWrapper>
        <TopBar />
        <StyledCenteredContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load your reader profiles. Please try again.
          </Alert>
          <Button primary onClick={() => router.reload()}>
            Retry
          </Button>
        </StyledCenteredContent>
      </StyledPageWrapper>
    )
  }

  if (!readerProfiles || readerProfiles.length === 0) {
    return (
      <StyledPageWrapper>
        <TopBar />
        <StyledCenteredContent>
          <Typography variant="h5" gutterBottom>
            Create a Reader Profile First
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You need to create a reader profile (for your child) before creating a story.
            This helps us personalize the story for them!
          </Typography>
          <Button primary href="/profile">
            Create Reader Profile
          </Button>
        </StyledCenteredContent>
      </StyledPageWrapper>
    )
  }

  if (readerProfiles.length > 1 && !selectedProfileId) {
    return (
      <StyledPageWrapper>
        <TopBar />
        <StyledCenteredContent>
          <StyledProfileSelector>
            <Typography variant="h5" gutterBottom>
              Who is this story for?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Select which child you&apos;d like to create a story for
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="profile-select-label">Select Reader</InputLabel>
              <Select
                labelId="profile-select-label"
                value={selectedProfileId || ''}
                label="Select Reader"
                onChange={(e) => setSelectedProfileId(e.target.value as number)}
              >
                {readerProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name} {profile.age ? `(${profile.age} years old)` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              primary
              disabled={!selectedProfileId}
              onClick={() => {
                /* Profile already selected, will trigger re-render */
              }}
            >
              Continue
            </Button>
          </StyledProfileSelector>
        </StyledCenteredContent>
      </StyledPageWrapper>
    )
  }

  if (!profileId) {
    return null
  }

  return (
    <StyledPageWrapper>
      <WizardContent readerProfileId={profileId} />
    </StyledPageWrapper>
  )
}

// --- TopBar component ---

interface TopBarProps {
  onBack?: () => void
  onReset?: () => void
}

const TopBar: React.FC<TopBarProps> = ({ onBack, onReset }) => (
  <StyledTopBar maxWidth="lg">
    {onBack ? (
      <IconButton onClick={onBack} aria-label="Back">
        <Icon icon={faArrowLeftLong} />
      </IconButton>
    ) : (
      <Link href="/library" style={{ textDecoration: 'none' }}>
        <IconButton aria-label="Back to library">
          <Icon icon={faArrowLeftLong} />
        </IconButton>
      </Link>
    )}
    {onReset && (
      <Button onClick={onReset}>
        Start Over
      </Button>
    )}
  </StyledTopBar>
)

// --- Inner wizard content (rendered after auth + profile selection) ---

interface WizardContentProps {
  readerProfileId: number
}

const WizardContent: React.FC<WizardContentProps> = ({ readerProfileId }) => {
  const router = useRouter()
  // Wizard state
  const [currentStep, setCurrentStep] = useState<StoryCreationStep>('characters')
  const [selectedCharacters, setSelectedCharacters] = useState<SelectedCharacters>({
    mainCharacterIds: [],
    supportingCharacterIds: [],
  })
  const [currentPlot, setCurrentPlot] = useState<PlotOutline | null>(null)
  const [selectedStyleId, setSelectedStyleId] = useState<number | null>(null)
  const [selectedPresetId, setSelectedPresetId] = useState<number | null>(null)
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string>('en')
  const [selectedConcept, setSelectedConcept] = useState<PremadeStoryConceptDetail | null>(null)

  // Session management
  const {
    session,
    sessionUuid,
    isLoading,
    isUpdating,
    isSubmitting,
    error: sessionError,
    isPremadePath,
    createSession,
    updateCharacters,
    updateArtStyle,
    updatePrintPreset,
    updateLanguage,
    updatePremadeStory,
    submitSession,
    clearSession,
  } = useStoryCreationSession({ readerProfileId })

  // --- Session recovery on refresh (Fix 2) ---
  const hasRecoveredRef = useRef(false)

  useEffect(() => {
    if (hasRecoveredRef.current || !session || isLoading || currentStep !== 'characters') {
      return
    }

    // Only recover once
    hasRecoveredRef.current = true

    // If session is expired, clear and start fresh
    if (session.is_expired) {
      clearSession()

      return
    }

    // If session is in a terminal or unrecoverable state, clear and start fresh
    const terminalStatuses: string[] = [
      StoryCreationSessionStatusEnum.COMPLETED,
      StoryCreationSessionStatusEnum.FINALIZING,
      StoryCreationSessionStatusEnum.CANCELLED,
      StoryCreationSessionStatusEnum.SLIDES_GENERATING,
    ]

    if (terminalStatuses.includes(session.status)) {
      clearSession()

      return
    }

    // Restore language from session
    if (session.language_code) {
      setSelectedLanguageCode(session.language_code)
    }

    // Restore art style from session
    if (session.art_style_id) {
      setSelectedStyleId(session.art_style_id)
    }

    // Restore print preset from session
    if (session.print_preset_id) {
      setSelectedPresetId(session.print_preset_id)
    }

    // Restore characters from session
    if (session.selected_character_ids) {
      const characterIds = session.selected_character_ids as number[]

      setSelectedCharacters({
        mainCharacterIds: characterIds,
        supportingCharacterIds: [],
      })
    }

    // Restore plot from session
    if (session.refined_plot_data) {
      setCurrentPlot(session.refined_plot_data)
    }

    // Determine the appropriate step based on session progression.
    // `art_style_id` is no longer a reliable signal because the wizard sets it
    // silently — we use `print_preset_id` and plot/premade selection instead.
    const status = session.status
    // Note: premade selection is held in local component state, not on the
    // session, so refresh recovery only knows about the AI plot path.
    const hasStoryChoice = !!session.refined_plot_data
    const isPlotInProgress = status === 'SLIDES_READY'
      || status === 'PLOT_READY'
      || status === 'PLOT_REFINING'

    if (session.print_preset_id) {
      setCurrentStep('submit')
    } else if (hasStoryChoice) {
      setCurrentStep('format')
    } else if (isPlotInProgress) {
      setCurrentStep('story')
    }
    // Otherwise stay on 'characters' (covers the language step too — light enough to redo)
  }, [session, isLoading, currentStep, clearSession])

  // Silent default art style — the wizard no longer surfaces a style picker,
  // but the backend requires `art_style_id` on the configure call. For the
  // premade path the backend derives it from `drawing_style_id`; for the AI
  // path we pre-pick the first available style here.
  const { data: drawingStylesData } = useDrawingStylesList()

  useEffect(() => {
    if (selectedStyleId !== null) return
    if (!sessionUuid) return

    const styles = drawingStylesData?.results ?? []

    if (styles.length === 0) return

    const defaultStyle = styles[0]

    setSelectedStyleId(defaultStyle.id)
    updateArtStyle(defaultStyle.id)
  }, [drawingStylesData, selectedStyleId, sessionUuid, updateArtStyle])

  // Keep local style state in sync with the session's art_style_id. The
  // backend overrides art_style_id when the user picks a premade story (to
  // match the style it was authored for), so the summary screen needs to
  // reflect the server's value rather than the silent default above.
  useEffect(() => {
    if (!session?.art_style_id) return
    if (session.art_style_id === selectedStyleId) return

    setSelectedStyleId(session.art_style_id)
  }, [session?.art_style_id, selectedStyleId])

  // Step: Characters -> Story
  const handleCharactersContinue = useCallback(async () => {
    const allCharacterIds = [
      ...selectedCharacters.mainCharacterIds,
      ...selectedCharacters.supportingCharacterIds,
    ]

    // Create session with characters if not exists
    if (!sessionUuid) {
      const newSession = await createSession({
        characterIds: allCharacterIds,
        languageCode: selectedLanguageCode,
      })

      if (newSession) {
        setCurrentStep('language')
      }
    } else {
      // Update existing session
      const updated = await updateCharacters(allCharacterIds)

      if (updated) {
        setCurrentStep('language')
      }
    }
  }, [selectedCharacters, selectedLanguageCode, sessionUuid, createSession, updateCharacters])

  // Step: Language -> Story
  const handleLanguageContinue = useCallback(() => {
    setCurrentStep('story')
  }, [])

  // Story step: user entered AI chat mode
  const handleEnterAIMode = useCallback(() => {
    updatePremadeStory(null)
    setSelectedConcept(null)
  }, [updatePremadeStory])

  // Story step -> Style (premade path)
  const handleStoryPremadeSelected = useCallback(
    async (concept: PremadeStoryConceptDetail) => {
      if (concept.matched_variant_id === null) {
        return
      }

      const updated = await updatePremadeStory(concept.matched_variant_id)

      if (updated) {
        setSelectedConcept(concept)
        setCurrentStep('format')
      }
    },
    [updatePremadeStory],
  )

  // Language change handler
  const handleLanguageChange = useCallback(
    (languageCode: string) => {
      setSelectedLanguageCode(languageCode)

      if (sessionUuid) {
        updateLanguage(languageCode)
      }
    },
    [sessionUuid, updateLanguage],
  )

  // Step: Plot -> Format
  const handlePlotContinue = useCallback((plot: PlotOutline) => {
    setCurrentPlot(plot)
    setCurrentStep('format')
  }, [])

  // Step: Format selection
  const handlePresetSelect = useCallback(
    async (presetId: number) => {
      setSelectedPresetId(presetId)
      await updatePrintPreset(presetId)
    },
    [updatePrintPreset],
  )

  // Step: Format -> Submit
  const handleFormatContinue = useCallback(() => {
    setCurrentStep('submit')
  }, [])

  // Step: Submit — return result so StepSubmission can use story_id
  const handleSubmit = useCallback(async (): Promise<SessionSubmitResponse> => {
    const result = await submitSession()

    clearSession()

    return result
  }, [submitSession, clearSession])

  const handleStyleSelect = useCallback(
    (styleId: number) => {
      setSelectedStyleId(styleId)
      updateArtStyle(styleId)
    },
    [updateArtStyle],
  )

  const handleBack = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep)

    if (currentIndex <= 0) {
      router.push('/library')

      return
    }

    setCurrentStep(STEP_ORDER[currentIndex - 1])
  }, [currentStep, router])

  const handleStartNewStory = useCallback(() => {
    clearSession()
    setCurrentStep('characters')
    setSelectedCharacters({ mainCharacterIds: [], supportingCharacterIds: [] })
    setCurrentPlot(null)
    setSelectedStyleId(null)
    setSelectedPresetId(null)
    setSelectedLanguageCode('en')
    setSelectedConcept(null)
    hasRecoveredRef.current = false
  }, [clearSession])

  return (
    <>
      <TopBar onBack={handleBack} onReset={handleStartNewStory} />
      <StyledWizardContainer>
        <Container maxWidth="lg">
          <StoryCreationProgress currentStep={currentStep} />

          <StyledStepContainer>
            {currentStep === 'characters' && (
              <StepCharacterSelection
                selectedCharacters={selectedCharacters}
                onSelect={setSelectedCharacters}
                onContinue={handleCharactersContinue}
              />
            )}

            {currentStep === 'language' && (
              <StepLanguageSelection
                selectedLanguageCode={selectedLanguageCode}
                onLanguageChange={handleLanguageChange}
                onContinue={handleLanguageContinue}
                isUpdating={isUpdating}
              />
            )}

            {currentStep === 'story' && sessionUuid && session && (
              <StepStorySelection
                sessionUuid={sessionUuid}
                characterCount={session.character_count}
                languageCode={selectedLanguageCode}
                onSelectPremade={handleStoryPremadeSelected}
                onPlotAccepted={handlePlotContinue}
                onAIModeEntered={handleEnterAIMode}
              />
            )}

            {currentStep === 'format' && (
              <StepFormatSelection
                selectedPresetId={selectedPresetId}
                onSelect={handlePresetSelect}
                onContinue={handleFormatContinue}
                isUpdating={isUpdating}
              />
            )}

            {currentStep === 'submit' && (
              <StepSubmission
                selectedCharacters={selectedCharacters}
                plot={currentPlot}
                selectedStyleId={selectedStyleId}
                selectedLanguageCode={selectedLanguageCode}
                session={session}
                onSubmit={handleSubmit}
                onSelectStyle={handleStyleSelect}
                isSubmitting={isSubmitting}
                isUpdating={isUpdating}
                error={sessionError}
                onStartNewStory={handleStartNewStory}
                isPremadePath={isPremadePath}
                selectedConcept={selectedConcept}
              />
            )}
          </StyledStepContainer>
        </Container>
      </StyledWizardContainer>
    </>
  )
}

// --- Styled components ---

const StyledPageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const StyledTopBar = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${pxToRem(12)} ${pxToRem(16)};
`

const StyledLoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 60vh;
`

const StyledCenteredContent = styled(Box)`
  text-align: center;
  padding: ${pxToRem(48)} ${pxToRem(24)};
  margin-top: ${pxToRem(64)};
  max-width: ${pxToRem(600)};
  margin-left: auto;
  margin-right: auto;
`

const StyledProfileSelector = styled(Box)`
  background-color: ${(props) => props.theme.palette.background.paper};
  border-radius: ${pxToRem(16)};
  padding: ${pxToRem(32)};
`

const StyledWizardContainer = styled(Box)`
  flex: 1;
  padding: 0 0 ${pxToRem(48)};
  background-color: ${(props) => props.theme.palette.background.default};
`

const StyledStepContainer = styled(Box)`
  padding-top: ${pxToRem(16)};
`

export default StoryCreationWizard
